import { api, type ContainerRow } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useConnectionOutcomes } from "./use-connection-outcomes";
import { ConnectionList, ConnectionRow, ConnectionSection } from "./ConnectionParts";
import { CONNECTION_GROUPS, CONNECTIONS, useConnectionStatuses } from "./connections-lib";

// A Boo's home: its Connections, grouped like Devin's Settings → Connections
// (Chat, Code, Work apps). Each row opens the connection's page. The
// catalog of other apps is the Integrations tab.
export function ConnectionsTab() {
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const containerId = params.containerId!;
  const { data: listResponse } = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const container = listResponse?.data.find((c) => c.id === containerId);
  // OAuth flows started before the connection pages existed still return here.
  useConnectionOutcomes(containerId);

  if (!container || !params.orgId) return null;
  return <ConnectionsList container={container} orgId={params.orgId} />;
}

function ConnectionsList({
  container,
  orgId,
}: {
  container: ContainerRow;
  orgId: string;
}) {
  const statuses = useConnectionStatuses(container);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <div className="flex w-full max-w-3xl flex-col gap-8 p-6">
        <div className="space-y-1">
          <h2 className="text-base font-semibold">Connections</h2>
          <p className="text-sm text-muted-foreground">
            What {container.name} is plugged into. Your workspace connects shared ones once; the rest belong to
            this Boo.
          </p>
        </div>
        {CONNECTION_GROUPS.map((group) => {
          const defs = CONNECTIONS.filter((c) => c.group === group.id && statuses[c.id]);
          if (defs.length === 0) return null;
          return (
            <ConnectionSection key={group.id} title={group.title} description={group.description(container.name)}>
              <ConnectionList>
                {defs.map((def) => (
                  <ConnectionRow
                    key={def.id}
                    def={def}
                    status={statuses[def.id]}
                    to="/orgs/$orgId/containers/$containerId/connections/$connection"
                    params={{ orgId, containerId: container.id, connection: def.id }}
                  />
                ))}
              </ConnectionList>
            </ConnectionSection>
          );
        })}
      </div>
    </div>
  );
}
